import { useState, useEffect, useCallback } from 'react';
import { PortvilleBillingService } from '../services/PortvilleBillingService';
import { PortvilleBill } from '../types/portvilleBilling';

export const usePortvilleBillingView = () => {
  const [bills, setBills] = useState<PortvilleBill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const billingService = PortvilleBillingService.getInstance();

  // UID로 청구서 조회
  const loadBillsByUid = useCallback(async (uid: string, year?: number, month?: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const userBills = await billingService.getBillsByUid(uid, year, month);
      setBills(userBills);
    } catch (err) {
      console.error('청구서 조회 오류:', err);
      setError(err instanceof Error ? err.message : '청구서를 불러오는 중 오류가 발생했습니다.');
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [billingService]);

  // 호실번호로 전화번호 조회 (SMS 인증용)
  const verifyRoomPhoneNumber = useCallback(async (roomNumber: string, inputPhoneNumber: string, year: number, month: number): Promise<boolean> => {
    try {
      const roomPhoneNumber = await billingService.getRoomPhoneNumber(roomNumber, year, month);
      
      if (!roomPhoneNumber) {
        return false; // 해당 연/월의 청구서에 입주자 정보가 없음
      }
      
      // 전화번호 정규화 (하이픈 제거, 공백 제거)
      const normalizePhoneNumber = (phone: string) => {
        return phone.replace(/[-\s]/g, '');
      };
      
      const normalizedRoomPhone = normalizePhoneNumber(roomPhoneNumber);
      const normalizedInputPhone = normalizePhoneNumber(inputPhoneNumber);
      
      return normalizedRoomPhone === normalizedInputPhone;
    } catch (err) {
      console.error('전화번호 검증 오류:', err);
      return false;
    }
  }, [billingService]);

  // 호실번호로 청구서 조회 (SMS 인증용)
  const loadBillsByRoom = useCallback(async (roomNumber: string, year?: number, month?: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const roomBills = await billingService.getBillsByRoomNumber(roomNumber, year, month);
      setBills(roomBills);
    } catch (err) {
      console.error('청구서 조회 오류:', err);
      setError(err instanceof Error ? err.message : '청구서를 불러오는 중 오류가 발생했습니다.');
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [billingService]);

  // 연/월 변경 시 자동 조회
  useEffect(() => {
    // 이 함수는 외부에서 호출되어야 함 (UID나 호실번호가 필요)
  }, [selectedYear, selectedMonth]);

  return {
    bills,
    loading,
    error,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth,
    loadBillsByUid,
    loadBillsByRoom,
    verifyRoomPhoneNumber,
    setLoading
  };
};
